/**
 * Putty URL grammar — pure builders shared by every client of the apex
 * (`putty.me`) host. One source of truth so the viewer, the notifier, the
 * iOS client, and any future surface all produce the same URLs.
 *
 * This file intentionally has zero runtime dependencies beyond the branded
 * ID types in `./id` — every builder is a pure path-only string formatter.
 * Window / store / React concerns (smart `absoluteUrl`, hooks that resolve
 * parent-slug context from a live cache) live in
 * `production/web-client/viewer/src/lib/urls.ts` and wrap these.
 *
 * URL grammar (canonical, from docs/plans/url-rename-and-slugs.plan.md +
 * docs/plans/standalone-collection-slugs.plan.md):
 *
 *   putty.me/                          home (stream)
 *   putty.me/collections               collections tab
 *   putty.me/b/{blockSlug}             block deeplink (always — short canonical)
 *   putty.me/c/{slug}                  standalone (parent-less) collection
 *                                      — slug is globally unique on its own
 *   putty.me/g/{entityId}              group OR layer-1 collection, member-only fallback
 *   putty.me/{name}                    profile (user) OR group (single global namespace, public)
 *   putty.me/{name}/{collectionSlug}   group-rooted child collection (public)
 *
 * Legacy (back-compat, still mounted in viewer `routes.tsx` so old links
 * keep resolving; the builders here **never** emit these):
 *
 *   /collection/:collectionId
 *   /group/:groupId
 *
 * `/folder/:folderId` used to live alongside these but is gone now that
 * the folder surface has been removed end-to-end (entity, repository,
 * route, shared types). Old folder deep-links 404 at the viewer.
 *
 * If you find yourself writing `` `/collection/${id}` `` anywhere, you're
 * almost certainly doing the wrong thing — use a builder.
 */
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/**
 * Apex viewer origin. Every absolute URL the server emits (notification
 * bodies, share links, etc.) is `${WEB_CLIENT_HOST}${path}`. Hardcoded to
 * the production host on purpose: even when the API runs in staging /
 * preview, notifications deliver to real users and should point at the
 * surface they actually use. The viewer's `absoluteUrl` wrapper overrides
 * this for local-dev / staging hosts where the developer is on a different
 * origin (`*.pages.dev`, `localhost`, etc.).
 */
export const WEB_CLIENT_HOST = "https://putty.me";
/**
 * The literal route-pattern strings used by `<Route path=...>` in the
 * viewer. Kept as a sealed `const` object so a typo in `routes.tsx` shows
 * up as a type error, and so a test can iterate over the canonical
 * surface.
 *
 * React Router's path syntax: `:param` for a named segment. These are
 * **pattern** strings, not interpolated values — use the builders below
 * to construct actual URLs.
 */
export const URL_PATHS = {
    home: "/",
    collectionsTab: "/collections",
    blockDeeplink: "/b/:blockSlug",
    standaloneCollection: "/c/:standaloneSlug",
    memberOnlyEntity: "/g/:entityId",
    name: "/:name",
    collectionBySlug: "/:name/:collectionSlug",
    // Legacy back-compat (mounted in routes.tsx, never produced by builders)
    legacyCollection: "/collection/:collectionId",
    legacyGroup: "/group/:groupId",
    // Reserved app surfaces (matched ahead of `:name` by React Router's
    // segment-specificity rules)
    components: "/components",
    experiment: "/exp/postit-lab",
    collectionLab: "/exp/collection-lab",
    dev: "/dev",
};
// ---------------------------------------------------------------------------
// Builders (entity → path-only URL)
// ---------------------------------------------------------------------------
//
// Each builder returns a path-only string (no scheme/host). Wrap with
// `absoluteUrl` when you need a full `https://putty.me/...` URL for
// share-sheet / "Copy link" UX or server-emitted notification bodies.
//
// The "smart" builders (`urlForCollection`, `urlForGroup`, `urlForBlock`)
// follow one rule: **prefer the slug-based URL when both halves of the
// slug pair are available; fall back to the internal-ID URL otherwise.**
// The fallback URL (`/g/{id}`) is the auth-gated member-only route, so
// it's always safe — it 404s for non-members instead of leaking IDs.
/**
 * `/b/{slug}` — block deeplink. Blocks always get a random 8-char slug at
 * creation time (post-backfill), so this should always succeed; if the
 * slug really is missing the caller can fall through to the containing
 * collection's URL.
 */
export function urlForBlock(block) {
    if (block.slug)
        return `/b/${block.slug}`;
    return undefined;
}
/**
 * Smart collection URL. Returns the public slug URL when the inputs
 * support it, otherwise the member-only `/g/{id}` fallback.
 *
 * Three URL shapes, in priority order:
 *
 *   1. `/{parentSlug}/{collectionSlug}` — child collection whose parent
 *      has a public slug (group-rooted with non-implicit Group parent).
 *   2. `/c/{collectionSlug}`            — truly standalone collection
 *      (`parentCollectionId === undefined`); the slug carries its own
 *      random suffix and is globally unique.
 *   3. `/g/{id}`                        — member-only fallback. Used
 *      when the collection has no slug yet, AND for any child
 *      collection whose parent's slug isn't available (Primary-rooted
 *      rows, implicit-Group children, cold-cache parent lookups).
 *
 * Critically, `/c/{slug}` is **only** emitted for parent-less rows.
 * A child collection's slug is unique only within its parent's
 * namespace — the slug "kchang-me" under one Group is unrelated to
 * "kchang-me" under another, and neither is the same row as a
 * standalone "kchang-me-a1b2c3" with the same prefix. Routing a child
 * collection through `/c/{slug}` either 404s (resolver scopes to
 * parent-less rows) or worst-case returns the wrong collection, so we
 * fall through to `/g/{id}` instead.
 *
 * For consumers that have a `CollectionItem` and want the URL "the right
 * way" without manually plumbing context, see `useUrlForCollection` in the
 * viewer's `src/lib/urls.ts`.
 */
export function urlForCollection(collection, ctx = {}) {
    if (collection.slug !== undefined && collection.slug.length > 0) {
        if (ctx.parentSlug !== undefined && ctx.parentSlug.length > 0) {
            return `/${ctx.parentSlug}/${collection.slug}`;
        }
        if (collection.parentCollectionId === undefined) {
            return `/c/${collection.slug}`;
        }
    }
    return `/g/${collection.id}`;
}
/**
 * Smart group URL. Returns `/{slug}` when the group has a public slug,
 * otherwise the member-only `/g/{id}` fallback.
 *
 * Implicit groups never have public slugs (auto-named, member-only by
 * construction) — they always land on `/g/{id}`.
 */
export function urlForGroup(group) {
    if (group.isImplicit !== true &&
        group.slug !== undefined &&
        group.slug.length > 0) {
        return `/${group.slug}`;
    }
    return `/g/${group.id}`;
}
/**
 * `/{username}` — profile URL. Returns `undefined` when the account hasn't
 * claimed a username yet; callers should hide the affordance or surface
 * the username-claim modal instead of producing a broken link.
 */
export function urlForUsername(username) {
    if (username === undefined || username.length === 0)
        return undefined;
    return `/${username}`;
}
/**
 * `/g/{id}` — explicit member-only fallback URL. Use this when you
 * specifically want the auth-gated path (e.g. you know the entity has no
 * public slug, you're rendering an in-app navigation tab, or you're the
 * notifier emitting a deep link that should resolve for every recipient
 * regardless of slug presence).
 *
 * In most cases prefer `urlForCollection` / `urlForGroup` which pick the
 * best URL automatically.
 */
export function urlForMemberOnlyEntity(entityId) {
    return `/g/${entityId}`;
}
// ---------------------------------------------------------------------------
// Absolute URL
// ---------------------------------------------------------------------------
/**
 * Join a path-only URL with a host to produce a full `https://.../path`
 * string. Pure: takes the host explicitly so this function is safe on the
 * server (no `window` reads).
 *
 * Defaults to `WEB_CLIENT_HOST` (production apex) — server callers can
 * rely on the default; viewer callers go through the smart wrapper in
 * `src/lib/urls.ts` which inspects `window.location.origin` to keep
 * staging / local-dev links pointing at the running viewer.
 */
export function absoluteUrl(path, host = WEB_CLIENT_HOST) {
    return `${host}${path}`;
}
